import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Building, Lock, Mail } from "lucide-react";

interface TendererAuthForm {
  email: string;
  accessCode: string;
}

export default function TendererAuth() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<TendererAuthForm>({
    email: '',
    accessCode: ''
  });
  const [error, setError] = useState<string>('');

  const authMutation = useMutation({
    mutationFn: async (data: TendererAuthForm) => {
      const response = await apiRequest('/api/tenderer/authenticate', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email.trim(),
          accessCode: data.accessCode.trim()
        })
      });
      return response;
    },
    onSuccess: (data: any) => {
      // Store authentication data in session storage
      const authData = {
        email: formData.email,
        accessCode: formData.accessCode,
        companyName: data.companyName,
        authenticatedAt: new Date().toISOString()
      };
      sessionStorage.setItem('tendererAuth', JSON.stringify(authData));
      
      toast({
        title: "Authentication Successful",
        description: `Welcome, ${data.companyName}! Redirecting to your dashboard...`,
      });
      
      // Fetch accessible projects and redirect to unified URL
      fetch(`/api/tenderer/accessible-projects?email=${encodeURIComponent(formData.email)}&accessCode=${encodeURIComponent(formData.accessCode)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.json())
        .then(projects => {
          if (projects && projects.length > 0) {
            const defaultProject = projects[0];
            const redirectUrl = `/dashboard/${defaultProject.id}/tenderer/${formData.accessCode}`;
            setLocation(redirectUrl);
          } else {
            setError('No accessible projects found. Please contact your administrator.');
          }
        })
        .catch(error => {
          console.error('Failed to fetch projects:', error);
          setError('Failed to load projects. Please try again.');
        });
    },
    onError: (error: any) => {
      setError(error.message || 'Invalid email or access code. Please check your credentials.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email.trim() || !formData.accessCode.trim()) {
      setError('Please enter both email and access code.');
      return;
    }
    
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    
    if (formData.accessCode.length !== 8) {
      setError('Access code must be 8 digits.');
      return;
    }
    
    authMutation.mutate(formData);
  };

  const handleInputChange = (field: keyof TendererAuthForm, value: string) => {
    // Auto-convert email to lowercase
    const processedValue = field === 'email' ? value.toLowerCase() : value;
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    if (error) setError(''); // Clear error when user starts typing
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mb-6">
            <Building className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            HG Procurement Hub
          </h1>
          <p className="text-gray-600 text-lg">
            Secure access to your assigned tender packages
          </p>
        </div>

        {/* Main Card */}
        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="space-y-2 pb-6">
            <CardTitle className="text-xl font-semibold text-gray-800 text-center">
              Authentication Required
            </CardTitle>
            <CardDescription className="text-gray-600 text-center">
              Enter your company credentials to access your tender packages
            </CardDescription>
          </CardHeader>
        
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-3">
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                  Company Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@company.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="pl-12 h-12 text-base border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                    disabled={authMutation.isPending}
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="accessCode" className="text-sm font-semibold text-gray-700">
                  8-Digit Access Code
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                  <Input
                    id="accessCode"
                    type="text"
                    placeholder="12345678"
                    value={formData.accessCode}
                    onChange={(e) => {
                      // Only allow numbers and limit to 8 digits
                      const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                      handleInputChange('accessCode', value);
                    }}
                    className="pl-12 h-12 text-base font-mono tracking-wider border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                    disabled={authMutation.isPending}
                    maxLength={8}
                  />
                </div>
                <p className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                  💡 This code was provided when the tender package was sent to your company
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
                disabled={authMutation.isPending}
              >
                {authMutation.isPending ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Authenticating...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Lock className="w-5 h-5" />
                    <span>Access Tender Packages</span>
                  </div>
                )}
              </Button>
            </form>
            
            <div className="mt-8 text-center space-y-2">
              <p className="text-sm text-gray-500">
                Need help? Contact the project administrator
              </p>
              <p className="text-xs text-gray-400">
                Check your tender submission email for the correct access code
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Security Badge */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/60 rounded-full backdrop-blur-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600 font-medium">Secure Access Portal</span>
          </div>
        </div>
      </div>
    </div>
  );
}